import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Chapter from "./Chapter";
import { FRAMEWORKS } from "@/lib/data";
import { IDS } from "@/lib/ids";

const WEB3FORMS_URL = "https://api.web3forms.com/submit";
const ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

export default function DemoForm() {
  const [state, setState] = useState({ name: "", email: "", company: "", message: "", website: "" });
  const [selected, setSelected] = useState(new Set(["iso27001"]));
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { setStatus("error"); setErrorMsg("Please accept the Privacy Policy to submit."); return; }
    setStatus("submitting");
    setErrorMsg("");
    // honeypot
    if (state.website) { setStatus("success"); return; }

    const frameworksPursued = FRAMEWORKS.filter(f => selected.has(f.id)).map(f => f.name).join(", ");
    const payload = {
      access_key: ACCESS_KEY || "",
      subject: `NormShift demo request \u2014 ${state.name || state.email}`,
      from_name: "NormShift Website",
      name: state.name,
      email: state.email,
      company: state.company,
      frameworks_pursued: frameworksPursued,
      message: state.message,
      _replyto: state.email,
    };

    try {
      if (!ACCESS_KEY) {
        // Fallback: submit to backend endpoint so the lead isn't lost.
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backendUrl}/api/leads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: state.name, email: state.email, company: state.company, frameworks: frameworksPursued, message: state.message }),
        });
        if (!res.ok) throw new Error("Backend save failed");
        setStatus("success");
        return;
      }

      const res = await fetch(WEB3FORMS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success !== false) {
        setStatus("success");
      } else {
        throw new Error(data.message || "Submission failed");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Something went wrong. Try email instead: daksh@normshift.in");
    }
  };

  return (
    <section id="demo" className="relative py-14 md:py-20 hairline-t">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Chapter number="12" label="Request a demo" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="font-display text-[36px] md:text-[54px] leading-[1.04] tracking-[-0.022em] text-balance"
            >
              Book a demo. See your <span className="grad-text">compliance crosswalk</span>, live.
            </motion.h2>
            <p className="mt-6 text-ink-muted text-[15px] leading-relaxed max-w-[46ch]">
              Watch one evidence set map to every control across 20 frameworks &mdash; ISO 27001, SOC 2, GDPR, DPDP and more. We respond within one business day.
            </p>

            <div className="mt-8 space-y-3 font-mono text-[12px] text-white/70">
              <div className="flex items-center gap-3"><span className="text-white/40">&#9993;</span><a href="mailto:daksh@normshift.in" className="hover:text-white">daksh@normshift.in</a></div>
              <div className="flex items-center gap-3"><span className="text-white/40">&#8614;</span>Mumbai &middot; India &middot; Global</div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  data-testid={IDS.demoSuccess}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="machined p-10 md:p-14 grad-border"
                  style={{ borderRadius: 14 }}
                >
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-brand-red">Received</div>
                  <div className="mt-5 font-display text-[28px] md:text-[36px] leading-tight tracking-[-0.02em]">Thanks &mdash; we'll email you to schedule.</div>
                  <p className="mt-4 text-ink-muted text-[14.5px] max-w-[52ch]">A note is on its way to <span className="text-white">{state.email}</span>. If you don't hear back within a business day, ping daksh@normshift.in directly.</p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  data-testid={IDS.demoForm}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="machined p-6 md:p-8"
                >
                  {/* honeypot */}
                  <input type="text" name="website" value={state.website} onChange={(e) => setState({ ...state, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Name" testId={IDS.demoNameField} value={state.name} onChange={(v) => setState({ ...state, name: v })} />
                    <Field label="Work email *" required testId={IDS.demoEmailField} type="email" value={state.email} onChange={(v) => setState({ ...state, email: v })} />
                  </div>
                  <div className="mt-4">
                    <Field label="Company" testId={IDS.demoCompanyField} value={state.company} onChange={(v) => setState({ ...state, company: v })} />
                  </div>

                  <div className="mt-6">
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted mb-3">Which frameworks are you pursuing?</div>
                    <div className="flex flex-wrap gap-2">
                      {FRAMEWORKS.map((f) => {
                        const on = selected.has(f.id);
                        return (
                          <button
                            type="button"
                            key={f.id}
                            data-testid={IDS.demoFrameworkChip(f.id)}
                            onClick={() => toggle(f.id)}
                            className={`px-3 py-1.5 rounded-full border font-mono text-[10.5px] tracking-[0.04em] transition-[transform,border-color,background,color] duration-300 hover:-translate-y-[2px] ${on ? "border-white/25 bg-white/[0.06] text-white" : "border-white/10 bg-transparent text-white/45 hover:text-white/75"}`}
                          >
                            {f.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block">
                      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted mb-2">Anything you want us to know</div>
                      <textarea
                        data-testid={IDS.demoMessageField}
                        rows={3}
                        value={state.message}
                        onChange={(e) => setState({ ...state, message: e.target.value })}
                        className="w-full bg-transparent border border-white/12 focus:border-white/30 rounded-md px-3 py-2.5 text-[14px] text-white outline-none transition-colors resize-none"
                        placeholder="e.g. Aiming for ISO 27001 + DPDP by Q2 2026"
                      />
                    </label>
                  </div>

                  <div className="mt-6">
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        data-testid="demo-form-consent"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        required
                        className="mt-[3px] w-4 h-4 accent-brand-red flex-shrink-0"
                      />
                      <span className="text-[12.5px] text-white/70 leading-relaxed">
                        I agree to be contacted about my demo request and accept the{" "}
                        <Link to="/privacy" className="text-white underline underline-offset-4 hover:text-brand-blue transition-colors">Privacy Policy</Link>.
                      </span>
                    </label>
                  </div>

                  <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">Founder-replied &middot; within 1 business day</div>
                    <button
                      type="submit"
                      data-testid={IDS.demoSubmit}
                      disabled={status === "submitting" || !state.email || !consent}
                      className="cta disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {status === "submitting" ? "Sending\u2026" : "Request a demo"}
                      <span aria-hidden>{"\u2192"}</span>
                    </button>
                  </div>

                  <div className="mt-4 font-mono text-[10.5px] tracking-[0.06em] text-white/40">
                    By submitting you agree to our <Link to="/privacy" className="text-white/70 underline underline-offset-4">Privacy Policy</Link>.
                  </div>

                  {status === "error" && (
                    <div className="mt-4 font-mono text-[11px] text-brand-red">{errorMsg}</div>
                  )}
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted mb-2">{label}</div>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-white/12 focus:border-white/30 rounded-md px-3 py-2.5 text-[14px] text-white outline-none transition-colors"
      />
    </label>
  );
}
